import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { isEqual } from 'lodash';
import * as Yup from 'yup';
import {
  FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX,
  PROFILES_ADMIN_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../utils/constants';
import UserSVG from '../search/admin-dashboard-tables/assets/UserSVG';
import SellerReviewForm from './SellerReviewForm';
import { sellerReviewValidator } from './validators/form-validators';

interface Props {
  review: {
    id: number,
    message: string,
    rating: number,
    status: string,
    sellerName: string,
    sellerRoute: string,
    userName: string,
    author_id: number,
    seller_id: number,
  },
  canUpdateStatus: boolean,
}

const SellerReviewEdit: React.FC<Props> = ({
  review,
  canUpdateStatus,
}): ReactElement => {
  const [formValues, setFormValues] = useState<{
    message: string,
    rating: number,
    status: string,
  }>({
    message: review.message,
    rating: review.rating,
    status: review.status,
  });
  const [loadingError, setLoadingError] = useState('');
  const [loading, setLoading] = useState(false);

  const sellerReviewUpdateValidator = sellerReviewValidator
    .shape({
      status: Yup.string().required().oneOf([
        'pending',
        'verified',
        'declined',
      ]),
    });

  useEffect(() => {
    if (!review || !review.id || !review.message || !review.rating) {
      if (!loadingError) {
        setLoadingError('Could not load review form; check your connection and try again.');
      }
    } else if (loadingError) {
      setLoadingError('');
    }
  }, [review, loadingError]);

  const updateFormState = useCallback((newValues: object) => {
    const newFormState = {
      ...formValues,
      ...newValues,
    };
    if (!isEqual(newFormState, formValues)) {
      setFormValues(newFormState);
    }
  }, [formValues]);

  const onSubmit = useCallback(async (values) => {
    updateFormState(values);
    setLoading(true);

    const submitValues = {
      ...values,
    };
    axios.patch(
      `${FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX}/${review.id}`,
      submitValues,
      { headers: { Accept: 'application/json' } },
    )
      .then((response) => {
        if (response.data?.review || (response.status >= 200 && response.status <= 299)) {
          Swal.fire(
            'Success',
            'Review updated successfully!',
            'success',
          ).then(() => {
            setLoading(false);
          });
        }
      })
      .catch((e) => {
        // console.log(e);
        setLoading(false);
        Swal.fire(
          'Error',
          e?.message || 'Something went wrong; check your address and connection, then try again.',
          'error',
        );
      });
  }, [formValues, review]);

  return (
    <div className="bg-white">
      {loadingError
        ? (
          <div className="alert alert-danger">
            <p>{loadingError}</p>
          </div>
        ) : (
          <SellerReviewForm
            formTitle={`Update the review of the seller ${review.sellerName}`}
            validator={sellerReviewUpdateValidator}
            formValues={formValues}
            canUpdateStatus={canUpdateStatus}
            loading={loading}
            onSubmit={onSubmit}
            FooterComponent={() => (
              <div className="px-4">
                <p className="d-flex align-items-center">
                  <span className="font-weight-semi-bold mr-2">Review Of: </span>
                  <a
                    className="m-0 text-dark-btn-txt font-weight-bold d-flex align-items-center"
                    href={
                      canUpdateStatus
                        ? `${PROFILES_ADMIN_PREFIX}/${review.seller_id}`
                        : review.sellerRoute
                    }
                  >
                    <span
                      className="d-flex mr-1"
                      style={{
                        width: '16px',
                        height: '16px',
                      }}
                    >
                      <UserSVG />
                    </span>
                    {review.sellerName}
                  </a>
                </p>
                {
                  canUpdateStatus && (
                    <p>
                      <span className="font-weight-semi-bold">Reviewed By: </span>
                      <a className="m-0 text-dark-btn-txt font-weight-bold" href={`${USER_ADMIN_PREFIX}/${review.author_id}`}>{review.userName}</a>
                    </p>
                  )
                }
              </div>
            )}
          />
        )}
    </div>
  );
};

export default SellerReviewEdit;

if (document.getElementById('react-seller-review-edit')) {
  const element = document.getElementById('react-seller-review-edit');
  const review = element?.dataset.review
    ? JSON.parse(element.dataset.review)
    : undefined;
  const canUpdateStatus: boolean = !!element?.dataset.canupdatestatus;
  ReactDOM.render(
    <SellerReviewEdit
      review={review}
      canUpdateStatus={canUpdateStatus}
    />,
    document.getElementById('react-seller-review-edit'),
  );
}
